import React, { Component } from 'react';
import classNames from 'classnames';
import { translate } from '../services';
import { CustomContent } from '.';
import { PropTypes } from 'prop-types';

const ignoredClickTags = {
  'input': true,
  'svg': true,
  'rect': true,
  'path': true,
  'g': true
};

const HeaderColumn = ( { className, column, index, onClick } ) => {
  const clickHandler = () => onClick( column, index );
  return <div onClick={ clickHandler } key={ index } className={ className }>
    <span>{ column.title && translate( column.title ) }</span>
  </div>;
};

const Row = ( { children, index, isExpandable, onClick } ) => {
  const clickHandler = e => isExpandable && onClick( e, index );
  return <div onClick={ clickHandler } className="row" >
    { children }
  </div>;
};

export class List extends Component {
  constructor( props ) {
    super( props );
    this.state = {
      sortedBy: null,
      descending: false,
      filterValue: null,
      selectedRow: -1
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleSearchValueChanged = e => {
    const { layout, onFilterChanged } = this.props;
    const { enableLocalFilter } = layout;
    
    if ( e.keyCode === 27 ) {
      // escape -> clear the inputbox
      this.searchInput.value = '';
    }

    e.preventDefault();
    setTimeout( function ( value ) {
      if (!this.mounted) {
        return;
      }
      if ( value === this.searchInput.value ) {
        // only change the value after sufficient time has elapsed
        // no need to search every input of the user
        this.setState( { filterValue: this.searchInput.value }, () => {
          !enableLocalFilter && onFilterChanged && onFilterChanged( this.state.filterValue );
        } );
      }
    }.bind( this, this.searchInput.value ), 300 );
  }

  handleColumnClicked = ( column, index ) => {
    const { sortedBy, descending } = this.state;

    if ( !column.isSortable ) {
      return;
    }

    this.setState( {
      sortedBy: index,
      descending: sortedBy === index ? !descending : false
    } );
  }

  handleRowClicked = ( e, rowIndex ) => {
    if ( ignoredClickTags[e.target.tagName.toLowerCase()] ) {
      return;
    }

    this.setState( { selectedRow: this.state.selectedRow === rowIndex ? -1 : rowIndex } );
  }

  getColumnClass( column, index ) {
    let { sortedBy, descending } = this.state;
    
    return classNames( `width-${column.width}`, { 
      'sortable': column.isSortable, 
      'sorted': sortedBy === index && !descending,
      'sorted-desc': sortedBy === index && descending,
      'quantity-column': column.title==="Quantity"
    } );
  }

  renderHeader( columns ) {
    if( !this.props.layout.enableHeader ) {
      return null;
    }
    
    let headerColumns = columns.map( ( column, index ) =>
      <HeaderColumn onClick={ this.handleColumnClicked } key={ index } column={ column } index={ index } className={ this.getColumnClass( column, index ) }/>
    );

    return <div className="row list-header">
      { headerColumns }
    </div>;
  }

  setSearchInput = input => this.searchInput = input

  renderToolbar() {
    return !this.props.layout.enableToolbar ? null : <div className="row toolbar">
      <div className="col-12 right">
        <input 
          ref={ this.setSearchInput }
          placeholder={ translate( 'Filter contents by' ) }
          onKeyUp={ this.handleSearchValueChanged }
          className="search-input"
        />
      </div>
    </div>;
  }

  renderCell( column, item, columnIndex, itemIndex ) {
    return <div key={ itemIndex * columnIndex + columnIndex } 
    // className={ 'width-' + column.width }
      className={this.getColumnClass(column, itemIndex)}
    >
      <CustomContent layout={ column } data={ item }/>
    </div>;
  }

  renderRow( columns, item, itemIndex ) {
    const { detailTemplate, isExpandable, template } = this.props;
    const DetailComponent = detailTemplate;
    const RowComponent = template;
    const className = classNames( 'item-row', { 'clickable': detailTemplate } );
    
    let newColumns = [...columns]
    if(this.props.className==="mobile-list-view" && Array.isArray(columns)){
      newColumns[1] = <div className="mobile-product-detail">{
        newColumns.slice(1, columns.length).map(child=>child)
      }</div>     
    }

    let displayColumns = this.props.className==="mobile-list-view" && Array.isArray(columns)?newColumns.slice(0,2): columns 
    /* in case it has a detail template, it should be wrapped with another div */
    return <div key={ itemIndex } className={ className }>
      <Row onClick={ this.handleRowClicked } index={ itemIndex } isExpandable={ isExpandable && isExpandable( item ) && detailTemplate }>
        { RowComponent 
          ? <RowComponent fromModelConfiguratorSelector={this.props.fromModelConfiguratorSelector} columns={ displayColumns } item={ item } itemIndex={ itemIndex }/> 
          : displayColumns.map( ( column, key ) => this.renderCell( column, item, key, itemIndex ) ) }
      </Row>
      { isExpandable && isExpandable( item ) && detailTemplate && this.state.selectedRow === itemIndex && <DetailComponent { ...item } /> }
    </div>;
  }

  renderContent( columns, items ) {
    let { filterValue, sortedBy, descending } = this.state;
    let { predicate, layout, noItemsFound } = this.props;
    const { enableLocalFilter } = layout;
    let rows = items;

    if ( filterValue && enableLocalFilter ) {
      rows = items.filter( item => predicate( item, filterValue ) );
    }

    if ( sortedBy ) {
      rows = [...rows].sort( ( a, b ) => ( a[columns[sortedBy].property] > b[columns[sortedBy].property] ? 1 : -1 ) * ( descending ? -1 : 1 ) );
    }

    return !rows || !rows.length ? noItemsFound ? React.cloneElement( noItemsFound ) : [] : rows.map( ( row, key ) => this.renderRow( columns, row, key ) );
  }

  onScroll = e => {
    this.props.onScroll && this.props.onScroll( e );
  }

  render() {
    let { layout, items } = this.props;

    return <div className="list" onScroll={ this.onScroll }>
      { this.renderHeader( layout.columns ) }
      { this.renderToolbar() }
      <div className="rows">
        { this.renderContent( layout.columns, items ) }
      </div>
    </div>;
  }
}

List.propTypes = {
  detailTemplate: PropTypes.oneOfType( [PropTypes.node, PropTypes.func, PropTypes.elementType] ),
  getCustomLayout: PropTypes.func.isRequired,
  isExpandable: PropTypes.func,
  items: PropTypes.arrayOf( PropTypes.oneOfType( [PropTypes.node, PropTypes.object] ) ), 
  layout: PropTypes.shape( {
    columns: PropTypes.array,
    enableHeader: PropTypes.bool,
    enableToolbar: PropTypes.bool,
    enableLocalFilter: PropTypes.bool
  } ),
  noItemsFound: PropTypes.oneOfType( [PropTypes.node, PropTypes.func, PropTypes.elementType] ),
  onScroll: PropTypes.func,
  predicate: PropTypes.func,
  template: PropTypes.oneOfType( [PropTypes.node, PropTypes.func, PropTypes.elementType] ),
  useCustomLayout: PropTypes.func,
  onFilterChanged: PropTypes.func
};